import * as d3 from 'd3';
import React, { useEffect, useRef } from "react";
import styles from "./DistanceScaleBar.module.scss"

export interface DistanceScaleBarLayoutProps {
  // zoom : number;
  width : number;
  height : number;
  // dimensions: [number, number, number];
  // pixelSpacing : number;
  pixelsPerMM? : number;
  negativeColor:boolean;
}

function computeRulerSize(
  barHeightPx: number,
  // pixelSpacing: number,
  // zoom: number
  pixelsPerMM: number,
): { sizePx: number; label: string; unit: 'mm'|'µm', tickStepMM: number } {

  const maxMM = barHeightPx / pixelsPerMM;
// 100mm 이상 → 10mm 단위
  if (maxMM >= 100) {
    const mm = Math.floor(maxMM / 10) * 10;
    const sizePx = mm * pixelsPerMM;
    return {
      sizePx,
      label: `${mm} mm`,
      unit: 'mm',
      tickStepMM: 10,
    };
  }

  // 10mm 이상 → 1mm 단위
  if (maxMM >= 10) {
    const mm = Math.floor(maxMM / 10) * 10;
    const sizePx = mm * pixelsPerMM;
    return {
      sizePx,
      label: `${mm} mm`,
      unit: 'mm',
      tickStepMM: 1,
    };
  }

  // 10mm 미만 → µm 단위
  const umPerPixel = (1 / pixelsPerMM) * 1000; // 1픽셀 몇 µm
  const maxUM = barHeightPx * umPerPixel;
  const um = Math.floor(maxUM / 10) * 10; // 10µm 단위 스냅
  const sizePx = (um / 1000) * pixelsPerMM; // µm → mm 변환 후 px로
  return {
    sizePx,
    label: `${um} µm`,
    unit: 'µm',
    tickStepMM: um / 1000, // tickStep을 mm 단위로 환산
  };
}

function DistanceScaleBarLayout({
  // zoom,
  width,
  height,
  // dimensions,
  // pixelSpacing
  pixelsPerMM,
  negativeColor
}: DistanceScaleBarLayoutProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!width || !height || !pixelsPerMM || isNaN(pixelsPerMM)) {
      return;
    }
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const barHeightPx = height/2;
    const barX = width - 6;
    const { sizePx, label,tickStepMM,unit } = computeRulerSize(barHeightPx, pixelsPerMM);
    const barY = (height / 2) - (sizePx / 2);
    const stepPx = tickStepMM * pixelsPerMM;
    const tickCount = Math.round(sizePx / stepPx);

    const defs = svg.append('defs')
    const filter = defs.append('filter').attr('id','blur')
    .attr('width','200%')
    .attr('height','200%')
    filter.append('feDropShadow')
      .attr('dx',0)
      .attr('dy',0)
      .attr('stdDeviation',1.5)
      .attr('flood-opacity',1.0)

    svg.append("line")
      .attr("x1", barX)
      .attr("x2", barX)
      .attr("y1", barY)
      .attr("y2", barY + sizePx)
      .attr("stroke", negativeColor?"#888888":"#FFFFFF")
      .attr("stroke-width", 1);

    // const mmCount = Math.round(sizePx / pixelsPerMM);
    // console.log(mmCount,sizePx,pixelsPerMM)
    for (let i = 0; i <= tickCount; i++){
      // const tickY = barY + pixelsPerMM * i
      const y =  barY + i * stepPx;
      const isMajor = unit==='mm'
    ? (tickStepMM === 1 ? i % 10 === 0 : i % 10 === 0) // 1mm 기준일 때는 10mm마다, 10mm 기준일 때는 100mm마다 굵게
    : (i % 10 === 0); // µm일 때 10단위마다 굵게
      const strokeWidth = isMajor?2:1;

      // const isBig = 1%10 == 0;
      svg.append("line")
        .attr("x1", barX - (isMajor?11:6))
        .attr("x2", barX)
        .attr("y1", y)
        .attr("y2", y)
        .attr("stroke", 'black')
        .attr("stroke-width", strokeWidth*1.5)

      svg.append("line")
        .attr("x1", barX - (isMajor?10:5))
        .attr("x2", barX)
        .attr("y1", y)
        .attr("y2", y)
        .attr("stroke", negativeColor?"#888888":"#FFFFFF")
        .attr("stroke-width", strokeWidth);
      // if(isMajor && i!==0){
      //    svg.append("text")
      //     .text(unit === 'mm' ? `${i * tickStepMM} mm` : `${i * tickStepMM * 1000} µm`)
      //     .attr("x", barX - 12)
      //     .attr("y", y)
      //     .attr("fill", "white")
      //     .attr("font-size", "12px")
      //     .attr("text-anchor", "end")
      //     .attr("dominant-baseline", "middle");
      // }
    }

    svg.append("text")
      .text(label)
      .attr("x", barX - 10)
      .attr("y", barY + sizePx)
      .attr("fill", negativeColor?"gray":"white")
      .attr("font-size", "16px")
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "middle")
      .attr("filter","url(#blur)");
  }, [width, height, pixelsPerMM,negativeColor]);

  return (
    <div className={styles['scalebar-container']}>
      <svg ref={svgRef} className={styles['scalebar-svg']} />
    </div>
  )
}

export default React.memo(DistanceScaleBarLayout)
